import Collapse from "components/Collapse";
import EmptyState from "components/EmptyState";
import Loader from "components/Loader";
import { ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useXtmQaReports } from "query/task.query";
import XtmQaReportLink from "./XtmQaReportLink";
import { useTaskXtmQAStore } from "store/useTaskXtmQAStore";
import { useEffect, useState, useRef } from "react";

interface Props {
  taskId: string;
}

// Define the type inline (matching the store):
type TaskBaseXtmQAReportState = {
  id: string;
  taskCode: string;
  toasterId: string;
  reportStatus: "INIT" | "IN_PROGRESS" | "FAILED";
};

export const XtmQaReportsTable = ({ taskId }: Props) => {
  const { t } = useTranslation();
  const { xtmQaReports, isLoading, error, refetch } = useXtmQaReports(taskId);
  const reportStatusesSize = useTaskXtmQAStore((state) => state.reportStatuses.size);

  const prevReportStatusesRef = useRef<Map<string, TaskBaseXtmQAReportState>>(new Map()); // ← Fixed type
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Listen to store state changes instead of hub directly
  useEffect(() => {
    // Get fresh reportStatuses from store inside useEffect
    const currentReportStatuses = useTaskXtmQAStore.getState().reportStatuses;

    const prevStatuses = prevReportStatusesRef.current;
    const currentStatuses = currentReportStatuses;

    const prevState = prevStatuses.get(taskId);
    const currentState = currentStatuses.get(taskId);

    // If task was in progress but is no longer in the map, generation finished
    if (prevState && !currentState) {
      void refetch(); // Refresh the list
    }

    // Update previous state
    prevReportStatusesRef.current = new Map(currentStatuses);
  }, [reportStatusesSize, taskId, refetch]); // ← Keep only reportStatusesSize

  if (isLoading) {
    return <Loader isShown={true} />;
  }

  const totalPages = Math.ceil(xtmQaReports.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentReports = xtmQaReports.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <Collapse
      defaultOpen={xtmQaReports.length < 10}
      header={
        <div className="flex items-center">
          <h5 className="my-2 space-x-2 font-medium">
            <span>{t("task.detail.xtmQaReports.title")}</span>
            <span>({xtmQaReports.length})</span>
          </h5>
        </div>
      }
    >
      {xtmQaReports.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={t("task.detail.xtmQaReports.title-no-reports")}
          subtitle={error ? t("error:common.server") : t("task.detail.xtmQaReports.title-no-reports-detail")}
        />
      ) : (
        <>
          <ul className="-my-4 ml-6 mt-2 list-none">
            {currentReports.map((xtmQaReport, index) => (
              <li key={`xtm-qa-report-${startIndex + index}`} className="my-2">
                <XtmQaReportLink
                  xtmQaReportName={xtmQaReport.xtmQaReportName}
                  xtmQaReportUrl={xtmQaReport.xtmQaReportUrl}
                />
              </li>
            ))}
          </ul>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between px-6">
              <div className="text-sm text-gray-500">
                {t("common.showing")} {startIndex + 1}-{Math.min(endIndex, xtmQaReports.length)} {t("common.of")}{" "}
                {xtmQaReports.length}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="flex items-center space-x-1 rounded-md px-2 py-1 text-sm font-medium text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft className="size-4" />
                  <span>{t("common.previous")}</span>
                </button>
                <span className="text-sm text-gray-500">
                  {t("common.page")} {currentPage} {t("common.of")} {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="flex items-center space-x-1 rounded-md px-2 py-1 text-sm font-medium text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span>{t("common.next")}</span>
                  <ChevronRight className="size-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Collapse>
  );
};
